import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, MapPin, Building2, Calendar, Filter,
  FileText, FileSpreadsheet, Truck, CheckCircle, Clock, TrendingUp
} from "lucide-react";
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart colors
const CHART_COLORS = ['#F97316', '#3B82F6', '#22C55E', '#EAB308', '#8B5CF6', '#EC4899', '#14B8A6', '#F43F5E'];

export default function AnalyticsTab({ drivers, sites }) {
  // Analytics state
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [deliveriesByDate, setDeliveriesByDate] = useState([]);
  const [driverPerformance, setDriverPerformance] = useState([]);
  const [driverLoads, setDriverLoads] = useState([]);
  const [siteStats, setSiteStats] = useState([]);
  const [customerStats, setCustomerStats] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Filter state
  const [analyticsStartDate, setAnalyticsStartDate] = useState("");
  const [analyticsEndDate, setAnalyticsEndDate] = useState("");
  const [analyticsFilterDrivers, setAnalyticsFilterDrivers] = useState([]);
  const [analyticsFilterSites, setAnalyticsFilterSites] = useState([]);
  const [analyticsFilterCustomers, setAnalyticsFilterCustomers] = useState([]);
  const [showDriverFilter, setShowDriverFilter] = useState(false);
  const [showSiteFilter, setShowSiteFilter] = useState(false);
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);

  // Get unique customers from customer stats
  const uniqueCustomers = customerStats.map(c => c.customer_name);

  useEffect(() => {
    // Fetch analytics on mount with small delay to ensure auth header is set
    const timer = setTimeout(() => {
      fetchAnalytics();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const fetchAnalytics = async (
    startDate = analyticsStartDate,
    endDate = analyticsEndDate,
    filterDrivers = analyticsFilterDrivers,
    filterSites = analyticsFilterSites,
    filterCustomers = analyticsFilterCustomers
  ) => {
    setAnalyticsLoading(true);
    try {
      const buildQueryString = () => {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (filterDrivers.length > 0) params.append('driver_ids', filterDrivers.join(','));
        if (filterSites.length > 0) params.append('site_ids', filterSites.join(','));
        if (filterCustomers.length > 0) params.append('customers', filterCustomers.join(','));
        return params.toString() ? `?${params.toString()}` : '';
      };
      const queryString = buildQueryString();
      
      // Use shorter timeout (30s) since queries are now optimized
      const config = { timeout: 30000 };

      // Fetch all analytics in parallel
      const [overviewRes, dateRes, driverRes, loadsRes, siteRes, customerRes] = await Promise.all([
        axios.get(`${API}/analytics/overview${queryString}`, config).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/deliveries-by-date?days=30${queryString.replace('?', '&')}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/driver-performance${queryString}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/driver-loads${queryString}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/site-stats${queryString}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/customer-stats${queryString}`, config).catch(() => ({ data: [] })),
      ]);

      if (overviewRes.data) setAnalyticsOverview(overviewRes.data);
      setDeliveriesByDate(dateRes.data || []);
      setDriverPerformance(driverRes.data || []);
      setDriverLoads(loadsRes.data || []);
      setSiteStats(siteRes.data || []);
      setCustomerStats(customerRes.data || []);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const applyAnalyticsDateFilter = () => {
    fetchAnalytics(analyticsStartDate, analyticsEndDate, analyticsFilterDrivers, analyticsFilterSites, analyticsFilterCustomers);
  };

  const clearAnalyticsDateFilter = () => {
    setAnalyticsStartDate("");
    setAnalyticsEndDate("");
    setAnalyticsFilterDrivers([]);
    setAnalyticsFilterSites([]);
    setAnalyticsFilterCustomers([]);
    fetchAnalytics("", "", [], [], []);
  };

  const toggleDriverFilter = (driverId) => {
    setAnalyticsFilterDrivers(prev =>
      prev.includes(driverId) ? prev.filter(id => id !== driverId) : [...prev, driverId]
    );
  };

  const toggleSiteFilter = (siteId) => {
    setAnalyticsFilterSites(prev =>
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  };

  const toggleCustomerFilter = (customer) => {
    setAnalyticsFilterCustomers(prev =>
      prev.includes(customer) ? prev.filter(c => c !== customer) : [...prev, customer]
    );
  };

  const exportAnalyticsToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Overview sheet
    if (analyticsOverview) {
      const overviewData = [
        { Metric: 'Total Deliveries', Value: analyticsOverview.total_deliveries },
        { Metric: 'Completion Rate (%)', Value: analyticsOverview.completion_rate },
        { Metric: 'Avg Delivery Time (min)', Value: analyticsOverview.avg_delivery_time_minutes },
        { Metric: 'Active Deliveries', Value: analyticsOverview.active_deliveries },
      ];
      const wsOverview = XLSX.utils.json_to_sheet(overviewData);
      XLSX.utils.book_append_sheet(wb, wsOverview, 'Overview');
    }

    // Deliveries by Date
    if (deliveriesByDate.length > 0) {
      const wsDate = XLSX.utils.json_to_sheet(deliveriesByDate.map(d => ({
        Date: d.date,
        Total: d.total,
        Completed: d.completed
      })));
      XLSX.utils.book_append_sheet(wb, wsDate, 'Deliveries by Date');
    }

    // Driver Performance
    if (driverPerformance.length > 0) {
      const wsDriver = XLSX.utils.json_to_sheet(driverPerformance.map(d => ({
        Driver: d.driver_name,
        'Completed Deliveries': d.completed_deliveries,
        'Avg Time (min)': d.avg_delivery_time_minutes
      })));
      XLSX.utils.book_append_sheet(wb, wsDriver, 'Driver Performance');
    }

    // Site Stats
    if (siteStats.length > 0) {
      const wsSite = XLSX.utils.json_to_sheet(siteStats.map(s => ({
        Site: s.site_name,
        'Total Deliveries': s.total_deliveries,
        'Completed': s.completed_deliveries
      })));
      XLSX.utils.book_append_sheet(wb, wsSite, 'Site Stats');
    }

    // Customer Stats
    if (customerStats.length > 0) {
      const wsCustomer = XLSX.utils.json_to_sheet(customerStats.map(c => ({
        Customer: c.customer_name,
        'Total Deliveries': c.total_deliveries,
        'Completed': c.completed_deliveries
      })));
      XLSX.utils.book_append_sheet(wb, wsCustomer, 'Customer Stats');
    }

    XLSX.writeFile(wb, `analytics_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Analytics exported to Excel!");
  };

  const exportAnalyticsToPDF = () => {
    const doc = new jsPDF();
    let yPos = 20;

    // Title
    doc.setFontSize(18);
    doc.text('Unity Delivery Analytics Report', 14, yPos);
    yPos += 10;

    // Date range
    doc.setFontSize(10);
    const dateRangeText = analyticsStartDate || analyticsEndDate
      ? `Date Range: ${analyticsStartDate || 'Start'} to ${analyticsEndDate || 'End'}`
      : 'Date Range: All Time';
    doc.text(dateRangeText, 14, yPos);
    yPos += 15;

    // Overview
    if (analyticsOverview) {
      doc.setFontSize(14);
      doc.text('Overview', 14, yPos);
      yPos += 8;
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Total Deliveries', analyticsOverview.total_deliveries],
          ['Completion Rate', `${analyticsOverview.completion_rate}%`],
          ['Avg Delivery Time', `${analyticsOverview.avg_delivery_time_minutes} min`],
          ['Active Deliveries', analyticsOverview.active_deliveries],
        ],
        theme: 'grid',
        headStyles: { fillColor: [249, 115, 22] },
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 50;
    }

    // Driver Performance
    if (driverPerformance.length > 0) {
      doc.setFontSize(14);
      doc.text('Driver Performance', 14, yPos);
      yPos += 8;
      autoTable(doc, {
        startY: yPos,
        head: [['Driver', 'Completed', 'Avg Time (min)']],
        body: driverPerformance.slice(0, 10).map(d => [d.driver_name, d.completed_deliveries, d.avg_delivery_time_minutes]),
        theme: 'grid',
        headStyles: { fillColor: [249, 115, 22] },
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 50;
    }

    // Add new page if needed
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Site Stats
    if (siteStats.length > 0) {
      doc.setFontSize(14);
      doc.text('Top Sites', 14, yPos);
      yPos += 8;
      autoTable(doc, {
        startY: yPos,
        head: [['Site', 'Total Deliveries', 'Completed']],
        body: siteStats.slice(0, 10).map(s => [s.site_name, s.total_deliveries, s.completed_deliveries]),
        theme: 'grid',
        headStyles: { fillColor: [249, 115, 22] },
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : yPos + 50;
    }

    // Add new page if needed
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Customer Stats
    if (customerStats.length > 0) {
      doc.setFontSize(14);
      doc.text('Deliveries by Customer', 14, yPos);
      yPos += 8;
      autoTable(doc, {
        startY: yPos,
        head: [['Customer', 'Total', 'Completed']],
        body: customerStats.map(c => [c.customer_name, c.total_deliveries, c.completed_deliveries]),
        theme: 'grid',
        headStyles: { fillColor: [249, 115, 22] },
      });
    }

    doc.save(`analytics_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Analytics exported to PDF!");
  };

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Date Filter and Export Controls */}
      <div className="bg-[#161920] border border-white/10 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-white/60" strokeWidth={2} />
            <span className="text-sm font-heading font-bold text-white/60 uppercase">Date Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={analyticsStartDate}
              onChange={(e) => setAnalyticsStartDate(e.target.value)}
              className="h-9 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm [color-scheme:dark]"
              data-testid="analytics-start-date"
            />
            <span className="text-white/40">to</span>
            <input
              type="date"
              value={analyticsEndDate}
              onChange={(e) => setAnalyticsEndDate(e.target.value)}
              className="h-9 px-3 bg-transparent border border-white/20 focus:border-primary focus:outline-none text-white text-sm [color-scheme:dark]"
              data-testid="analytics-end-date"
            />
          </div>
        </div>

        {/* Filter Dropdowns Row */}
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-white/60" strokeWidth={2} />
            <span className="text-sm font-heading font-bold text-white/60 uppercase">Filters:</span>
          </div>

          {/* Driver Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => { setShowDriverFilter(!showDriverFilter); setShowSiteFilter(false); setShowCustomerFilter(false); }}
              className={`h-9 px-3 border text-sm font-heading font-bold uppercase flex items-center gap-2 ${
                analyticsFilterDrivers.length > 0
                  ? 'bg-primary/20 border-primary text-primary'
                  : 'bg-transparent border-white/20 text-white/60 hover:border-white/40'
              }`}
              data-testid="driver-filter-dropdown"
            >
              <Users className="w-4 h-4" strokeWidth={2} />
              Drivers {analyticsFilterDrivers.length > 0 && `(${analyticsFilterDrivers.length})`}
            </button>
            {showDriverFilter && (
              <div className="absolute top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto bg-[#1a1d24] border border-white/20 z-50 shadow-lg">
                <div className="p-2 border-b border-white/10">
                  <button
                    onClick={() => setAnalyticsFilterDrivers([])}
                    className="text-xs text-white/60 hover:text-white"
                  >
                    Clear all
                  </button>
                </div>
                {drivers.map(driver => (
                  <label key={driver.id} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsFilterDrivers.includes(driver.id)}
                      onChange={() => toggleDriverFilter(driver.id)}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-white">{driver.full_name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Site Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => { setShowSiteFilter(!showSiteFilter); setShowDriverFilter(false); setShowCustomerFilter(false); }}
              className={`h-9 px-3 border text-sm font-heading font-bold uppercase flex items-center gap-2 ${
                analyticsFilterSites.length > 0
                  ? 'bg-primary/20 border-primary text-primary'
                  : 'bg-transparent border-white/20 text-white/60 hover:border-white/40'
              }`}
              data-testid="site-filter-dropdown"
            >
              <MapPin className="w-4 h-4" strokeWidth={2} />
              Sites {analyticsFilterSites.length > 0 && `(${analyticsFilterSites.length})`}
            </button>
            {showSiteFilter && (
              <div className="absolute top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto bg-[#1a1d24] border border-white/20 z-50 shadow-lg">
                <div className="p-2 border-b border-white/10">
                  <button
                    onClick={() => setAnalyticsFilterSites([])}
                    className="text-xs text-white/60 hover:text-white"
                  >
                    Clear all
                  </button>
                </div>
                {sites.map(site => (
                  <label key={site.id} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsFilterSites.includes(site.id)}
                      onChange={() => toggleSiteFilter(site.id)}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-white">{site.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Customer Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => { setShowCustomerFilter(!showCustomerFilter); setShowDriverFilter(false); setShowSiteFilter(false); }}
              className={`h-9 px-3 border text-sm font-heading font-bold uppercase flex items-center gap-2 ${
                analyticsFilterCustomers.length > 0
                  ? 'bg-primary/20 border-primary text-primary'
                  : 'bg-transparent border-white/20 text-white/60 hover:border-white/40'
              }`}
              data-testid="customer-filter-dropdown"
            >
              <Building2 className="w-4 h-4" strokeWidth={2} />
              Customers {analyticsFilterCustomers.length > 0 && `(${analyticsFilterCustomers.length})`}
            </button>
            {showCustomerFilter && (
              <div className="absolute top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto bg-[#1a1d24] border border-white/20 z-50 shadow-lg">
                <div className="p-2 border-b border-white/10">
                  <button
                    onClick={() => setAnalyticsFilterCustomers([])}
                    className="text-xs text-white/60 hover:text-white"
                  >
                    Clear all
                  </button>
                </div>
                {uniqueCustomers.map(customer => (
                  <label key={customer} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsFilterCustomers.includes(customer)}
                      onChange={() => toggleCustomerFilter(customer)}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm text-white">{customer}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={applyAnalyticsDateFilter}
            className="px-4 h-9 bg-primary text-white font-heading font-bold text-xs uppercase"
            data-testid="apply-date-filter"
          >
            Apply Filters
          </button>
          {(analyticsStartDate || analyticsEndDate || analyticsFilterDrivers.length > 0 || analyticsFilterSites.length > 0 || analyticsFilterCustomers.length > 0) && (
            <button
              onClick={clearAnalyticsDateFilter}
              className="px-4 h-9 bg-transparent border border-white/20 text-white/60 font-heading font-bold text-xs uppercase hover:border-white/40"
              data-testid="clear-date-filter"
            >
              Clear All
            </button>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <button
              onClick={exportAnalyticsToExcel}
              className="px-4 h-9 bg-accent-success/20 text-accent-success border border-accent-success/30 font-heading font-bold text-xs uppercase hover:bg-accent-success/30 flex items-center gap-2"
              data-testid="export-analytics-xlsx"
            >
              <FileSpreadsheet className="w-4 h-4" strokeWidth={2} />
              XLSX
            </button>
            <button
              onClick={exportAnalyticsToPDF}
              className="px-4 h-9 bg-red-500/20 text-red-400 border border-red-500/30 font-heading font-bold text-xs uppercase hover:bg-red-500/30 flex items-center gap-2"
              data-testid="export-analytics-pdf"
            >
              <FileText className="w-4 h-4" strokeWidth={2} />
              PDF
            </button>
          </div>
        </div>
      </div>

      {analyticsLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-white/60 font-body">Loading analytics...</div>
        </div>
      ) : (
        <>
          {/* Overview Cards */}
          {analyticsOverview && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-[#161920] border border-white/10 p-6">
                <div className="flex items-center justify-between mb-2">
                  <Truck className="w-8 h-8 text-primary" strokeWidth={2} />
                  <span className="font-mono text-3xl font-bold text-white">{analyticsOverview.total_deliveries}</span>
                </div>
                <p className="text-sm font-body text-white/60 uppercase tracking-wider">Total Deliveries</p>
              </div>
              <div className="bg-[#161920] border border-white/10 p-6">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle className="w-8 h-8 text-accent-success" strokeWidth={2} />
                  <span className="font-mono text-3xl font-bold text-accent-success">{analyticsOverview.completion_rate}%</span>
                </div>
                <p className="text-sm font-body text-white/60 uppercase tracking-wider">Completion Rate</p>
              </div>
              <div className="bg-[#161920] border border-white/10 p-6">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-8 h-8 text-accent-info" strokeWidth={2} />
                  <span className="font-mono text-3xl font-bold text-accent-info">{analyticsOverview.avg_delivery_time_minutes}</span>
                </div>
                <p className="text-sm font-body text-white/60 uppercase tracking-wider">Avg Time (min)</p>
              </div>
              <div className="bg-[#161920] border border-white/10 p-6">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-8 h-8 text-accent-warning" strokeWidth={2} />
                  <span className="font-mono text-3xl font-bold text-accent-warning">{analyticsOverview.active_deliveries}</span>
                </div>
                <p className="text-sm font-body text-white/60 uppercase tracking-wider">Active Now</p>
              </div>
            </div>
          )}

          {/* Deliveries Over Time Chart */}
          {deliveriesByDate.length > 0 && (
            <div className="bg-[#161920] border border-white/10 p-6">
              <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Deliveries (Last 30 Days)</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={deliveriesByDate}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#666" tick={{ fill: '#999' }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1d24', border: '1px solid #333', borderRadius: 0, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                    <Line type="monotone" dataKey="total" stroke="#F97316" strokeWidth={2} name="Total" dot={false} />
                    <Line type="monotone" dataKey="completed" stroke="#22C55E" strokeWidth={2} name="Completed" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Loads by Driver */}
            {driverLoads.length > 0 && (
              <div className="bg-[#161920] border border-white/10 p-6">
                <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Loads by Driver</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={driverLoads.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#666" tick={{ fill: '#999' }} />
                      <YAxis type="category" dataKey="driver_name" stroke="#666" tick={{ fill: '#999', fontSize: 11 }} width={100} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1d24', border: '1px solid #333', borderRadius: 0, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                      <Bar dataKey="loads" fill="#10B981" name="Loads" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 text-white/60 font-semibold uppercase text-xs">Driver</th>
                        <th className="text-left py-2 text-white/60 font-semibold uppercase text-xs">Truck</th>
                        <th className="text-right py-2 text-white/60 font-semibold uppercase text-xs">Loads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driverLoads.map((d) => (
                        <tr key={d.driver_id} className="border-b border-white/5">
                          <td className="py-2 text-white">{d.driver_name}</td>
                          <td className="py-2 text-white/60">{d.truck_number}</td>
                          <td className="py-2 text-right text-white font-mono font-bold">{d.loads}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Sites */}
            {siteStats.length > 0 && (
              <div className="bg-[#161920] border border-white/10 p-6">
                <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Top Sites</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={siteStats.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#666" tick={{ fill: '#999' }} />
                      <YAxis type="category" dataKey="site_name" stroke="#666" tick={{ fill: '#999', fontSize: 11 }} width={120} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1d24', border: '1px solid #333', borderRadius: 0, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                      <Bar dataKey="total_deliveries" fill="#3B82F6" name="Deliveries" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          {/* Customer Stats */}
          {customerStats.length > 0 && (
            <div className="bg-[#161920] border border-white/10 p-6">
              <h3 className="font-heading font-bold text-lg uppercase text-white mb-4">Deliveries by Customer</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={customerStats.slice(0, 8)}
                        dataKey="total_deliveries"
                        nameKey="customer_name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name.slice(0, 10)}${name.length > 10 ? '...' : ''} ${(percent * 100).toFixed(0)}%`}
                      >
                        {customerStats.slice(0, 8).map((_, index) => (
                          <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1a1d24', border: '1px solid #333', borderRadius: 0, color: '#fff' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 text-white/60 font-semibold uppercase text-xs">Customer</th>
                        <th className="text-right py-2 text-white/60 font-semibold uppercase text-xs">Total</th>
                        <th className="text-right py-2 text-white/60 font-semibold uppercase text-xs">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerStats.map((c, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="py-2 text-white">{c.customer_name}</td>
                          <td className="py-2 text-right text-white font-mono">{c.total_deliveries}</td>
                          <td className="py-2 text-right text-accent-success font-mono">{c.completed_deliveries}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
